import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Card,
  CardHeader,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";
import { useAuth } from "@/hooks/useAuth";
import { isSuperUser } from "@/types/user";
import MenuBar from "@/components/MenuBar";
import { Menu, HelpCircle, AlertTriangle, Info } from "lucide-react";
import { toast } from "@/components/ui/use-toast";

interface LeavePolicy {
  policyId: string;
  policyDisplayName: string;
  accrualPeriod: string;
  canHaveNegativeBalance: boolean;
  accrualStartFrom: string | null;
  requiresProof: boolean;
  numberOfHoursPerPeriod: number;
}

interface PtoPolicyResponse {
  leavePolicies: LeavePolicy[];
}

interface NegativeBalanceEmployee {
  id: number;
  name: string;
  currentBalance: number;
  projectedBalance: number;
}

interface ValidatePolicyResponse {
  hasNegativeBalances: boolean;
  affectedEmployees: NegativeBalanceEmployee[];
}

const PolicyConfig: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isSuperUserAccount = isSuperUser(user);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const [policy, setPolicy] = useState<LeavePolicy | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Get userId from URL query parameters if available
  const searchParams = new URLSearchParams(location.search);
  const userIdParam = searchParams.get("userId");
  const [selectedUserId, setSelectedUserId] = useState<string>(
    userIdParam || "current",
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationError, setValidationError] =
    useState<ValidatePolicyResponse | null>(null);
  const [originalPolicy, setOriginalPolicy] = useState<LeavePolicy | null>(
    null,
  );
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchPolicy();
  }, [id, selectedUserId]);

  const fetchPolicy = async () => {
    if (!id) {
      return;
    }
    try {
      const queryParams: Record<string, string | number> = {};

      // Add userId to query params if superuser and not viewing as current user
      if (isSuperUserAccount && selectedUserId !== "current") {
        queryParams.userId = selectedUserId;
      }

      const response = await ApiService.request<PtoPolicyResponse>(
        EndpointKeys.PTO_POLICY,
        {
          urlParams: { id },
          queryParams,
        },
      );
      console.log("response", response);
      setPolicy(response.leavePolicies[0]);
      setOriginalPolicy(response.leavePolicies[0]);
    } catch (error) {
      console.error("Error fetching policy:", error);
      setError("Failed to load policy. Please try again.");
    }
  };

  useEffect(() => {
    if (policy && originalPolicy) {
      const changed =
        policy.policyDisplayName !== originalPolicy.policyDisplayName ||
        policy.accrualPeriod !== originalPolicy.accrualPeriod ||
        policy.canHaveNegativeBalance !==
          originalPolicy.canHaveNegativeBalance ||
        policy.accrualStartFrom !== originalPolicy.accrualStartFrom ||
        policy.requiresProof !== originalPolicy.requiresProof ||
        Number(policy.numberOfHoursPerPeriod) !==
          Number(originalPolicy.numberOfHoursPerPeriod);

      setHasChanges(changed);
    }
  }, [policy, originalPolicy]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;
    setPolicy((prev) =>
      prev
        ? { ...prev, [name]: type === "checkbox" ? e.target.checked : value }
        : null,
    );
    // Clear validation errors when user makes changes
    setValidationError(null);
  };

  const handleSelectChange = (name: string, value: string) => {
    setPolicy((prev) => (prev ? { ...prev, [name]: value } : null));
    // Clear validation errors when user makes changes
    setValidationError(null);
  };

  const handleSwitchChange = (name: string, checked: boolean) => {
    setPolicy((prev) => (prev ? { ...prev, [name]: checked } : null));
    // Clear validation errors when user makes changes
    setValidationError(null);
  };

  const validatePolicyChanges = async () => {
    if (!policy) return;

    try {
      const queryParams: Record<string, string | number> = {};
      if (isSuperUserAccount && selectedUserId !== "current") {
        queryParams.userId = selectedUserId;
      }

      const response = await ApiService.request<ValidatePolicyResponse>(
        EndpointKeys.VALIDATE_PTO_POLICY,
        {
          method: "POST",
          urlParams: { id: policy.policyId },
          body: policy,
          queryParams,
        },
      );

      return response;
    } catch (error) {
      console.error("Error validating policy changes:", error);
      toast({
        title: "Error",
        description: "Failed to validate policy changes",
        variant: "destructive",
      });
      return null;
    }
  };

  const handleUpdate = async () => {
    if (!policy) return;

    setIsSubmitting(true);

    try {
      // Only perform validation if hours changed or accrual period changed
      if (
        originalPolicy &&
        (Number(policy.numberOfHoursPerPeriod) !==
          Number(originalPolicy.numberOfHoursPerPeriod) ||
          policy.accrualPeriod !== originalPolicy.accrualPeriod)
      ) {
        const validationResult = await validatePolicyChanges();

        // Always show the validation warning if there are negative balances
        // So the admin can see which employees would be affected
        if (validationResult?.hasNegativeBalances) {
          setValidationError(validationResult);

          // Only block submission if negative balances aren't allowed
          if (!policy.canHaveNegativeBalance) {
            setIsSubmitting(false);
            return;
          }
        }
      }

      const queryParams: Record<string, string | number> = {};
      if (isSuperUserAccount && selectedUserId !== "current") {
        queryParams.userId = selectedUserId;
      }

      await ApiService.request(EndpointKeys.UPDATE_PTO_POLICY, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        urlParams: { id: policy.policyId },
        body: policy,
        queryParams,
      });

      toast({
        title: "Success",
        description: "Policy updated successfully",
      });

      // Preserve userId in the URL when navigating back to time-off
      if (isSuperUserAccount && selectedUserId !== "current") {
        navigate(`/time-off?userId=${selectedUserId}`);
      } else {
        navigate("/time-off");
      }
    } catch (error) {
      console.error("Error updating policy:", error);
      setError("Failed to update policy. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!policy) return <div>Loading...</div>;

  return (
    <div className="flex min-h-screen bg-background">
      {/* Mobile Menu Toggle */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-30 flex items-center p-4 bg-gray-900">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="text-white"
        >
          <Menu className="h-6 w-6" />
        </Button>
      </div>

      {/* Sidebar */}
      <div
        className={`${
          isMobileMenuOpen ? "block" : "hidden"
        } md:block fixed md:relative w-64 h-full bg-gray-900 z-20`}
      >
        <MenuBar />
      </div>

      {/* Main Content */}
      <div className="flex-1 w-full md:w-auto mt-16 md:mt-0 px-4 md:px-6 py-4 md:py-6 bg-gray-100">
        <Card>
          <CardHeader>
            <h2 className="text-2xl font-bold">Configure Leave Policy</h2>
            {isSuperUserAccount && selectedUserId !== "current" && (
              <div className="text-sm text-blue-500">
                Viewing as user ID: {selectedUserId}
              </div>
            )}
          </CardHeader>
          <CardContent>
            <Alert className="mb-4 border-blue-200 bg-blue-50">
              <Info className="h-4 w-4 text-blue-600" />
              <AlertTitle className="text-blue-800">Important Note</AlertTitle>
              <AlertDescription className="text-blue-700">
                Changes made here can affect employee PTO balances and accruals.
                Please review carefully before saving.
              </AlertDescription>
            </Alert>

            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {validationError && (
              <Alert
                variant={
                  policy.canHaveNegativeBalance ? "default" : "destructive"
                }
                className={`mb-4 ${
                  policy.canHaveNegativeBalance
                    ? "bg-amber-50 border-amber-200"
                    : "bg-red-50 border-red-200"
                }`}
              >
                <AlertTriangle
                  className={`h-4 w-4 ${
                    policy.canHaveNegativeBalance
                      ? "text-amber-600"
                      : "text-red-600"
                  } mr-2`}
                />
                <div className="space-y-2">
                  <AlertDescription
                    className={`font-medium ${
                      policy.canHaveNegativeBalance
                        ? "text-amber-800"
                        : "text-red-800"
                    }`}
                  >
                    {policy.canHaveNegativeBalance
                      ? "Warning: This policy change will cause negative balances for some employees."
                      : "Warning: This policy change would cause negative balances for some employees."}
                  </AlertDescription>
                  <div className="mt-2 space-y-2">
                    <p
                      className={`text-sm ${
                        policy.canHaveNegativeBalance
                          ? "text-amber-700"
                          : "text-red-700"
                      }`}
                    >
                      {policy.canHaveNegativeBalance
                        ? "The following employees will have negative balances after this change:"
                        : "The following employees would have negative balances:"}
                    </p>
                    <Table className="border rounded-md">
                      <TableHeader
                        className={
                          policy.canHaveNegativeBalance
                            ? "bg-amber-100"
                            : "bg-red-100"
                        }
                      >
                        <TableRow>
                          <TableHead>Employee</TableHead>
                          <TableHead>Current Balance</TableHead>
                          <TableHead>Projected Balance</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {validationError.affectedEmployees.map((employee) => (
                          <TableRow key={employee.id}>
                            <TableCell>{employee.name}</TableCell>
                            <TableCell>
                              {employee.currentBalance} hours
                            </TableCell>
                            <TableCell
                              className={`${
                                policy.canHaveNegativeBalance
                                  ? "text-amber-700"
                                  : "text-red-600"
                              } font-medium`}
                            >
                              {employee.projectedBalance} hours
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {!policy.canHaveNegativeBalance && (
                      <p className="text-sm text-red-700 font-medium">
                        To proceed with this change, enable "Allow Negative
                        Balance" below.
                      </p>
                    )}
                    {policy.canHaveNegativeBalance && (
                      <p className="text-sm text-amber-700 font-medium">
                        You've enabled "Allow Negative Balance", so these
                        employees will be able to have negative balances. You
                        can continue with the changes.
                      </p>
                    )}
                  </div>
                </div>
              </Alert>
            )}

            <form className="space-y-4">
              <div>
                <Label htmlFor="policyDisplayName">Policy Name</Label>
                <Input
                  id="policyDisplayName"
                  name="policyDisplayName"
                  value={policy.policyDisplayName}
                  onChange={handleInputChange}
                />
              </div>
              <div>
                <Label htmlFor="numberOfHoursPerPeriod">
                  Number of Hours Per Period
                </Label>
                <Input
                  id="numberOfHoursPerPeriod"
                  name="numberOfHoursPerPeriod"
                  value={policy.numberOfHoursPerPeriod}
                  onChange={handleInputChange}
                />
              </div>
              <div>
                <div className="flex items-center space-x-2 mb-2">
                  <Label htmlFor="accrualPeriod">Accrual Period</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <HelpCircle className="h-4 w-4 text-gray-500" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-sm">
                        <p>
                          <strong>Yearly:</strong> Employee gets their entire
                          balance for the year upfront, even if starting
                          mid-year.
                        </p>
                        <p className="mt-2">
                          <strong>Monthly:</strong> Choose this for prorated
                          balances - PTO accrues monthly based on time worked.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Select
                  name="accrualPeriod"
                  value={policy.accrualPeriod}
                  onValueChange={(value) =>
                    handleSelectChange("accrualPeriod", value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select accrual period" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Monthly">Monthly</SelectItem>
                    <SelectItem value="Yearly">Yearly</SelectItem>
                    <SelectItem value="Unlimited">Unlimited</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div
                className={`flex items-center space-x-2 ${
                  validationError && !policy.canHaveNegativeBalance
                    ? "p-3 border border-red-300 rounded-md bg-red-50"
                    : ""
                }`}
              >
                <Switch
                  id="canHaveNegativeBalance"
                  checked={policy.canHaveNegativeBalance}
                  onCheckedChange={(checked) =>
                    handleSwitchChange("canHaveNegativeBalance", checked)
                  }
                  className={
                    validationError && !policy.canHaveNegativeBalance
                      ? "data-[state=unchecked]:bg-red-300"
                      : ""
                  }
                />
                <Label
                  htmlFor="canHaveNegativeBalance"
                  className={
                    validationError && !policy.canHaveNegativeBalance
                      ? "font-semibold text-red-800"
                      : validationError
                        ? "font-medium text-amber-800"
                        : ""
                  }
                >
                  Allow Negative Balance
                  {validationError && !policy.canHaveNegativeBalance && (
                    <span className="text-red-700 ml-2 text-sm">
                      (Required for this change)
                    </span>
                  )}
                </Label>
              </div>
              <div>
                <div className="flex items-center space-x-2 mb-2">
                  <Label htmlFor="accrualStartFrom">Accrual Start</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <HelpCircle className="h-4 w-4 text-gray-500" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-sm">
                        <p>
                          <strong>Calendar Year (Recommended):</strong> PTO
                          resets on January 1st.
                        </p>
                        <p className="mt-2">
                          <strong>Fiscal Year (Rare):</strong> Only select if
                          specifically negotiated - runs from April 5th to April
                          6th.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Select
                  name="accrualStartFrom"
                  value={policy.accrualStartFrom || ""}
                  onValueChange={(value) =>
                    handleSelectChange("accrualStartFrom", value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select accrual start" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Fiscal">Fiscal Year</SelectItem>
                    <SelectItem value="Calendar">Calendar Year</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="requiresProof"
                  checked={policy.requiresProof}
                  onCheckedChange={(checked) =>
                    handleSwitchChange("requiresProof", checked)
                  }
                />
                <Label htmlFor="requiresProof">Requires Proof</Label>
              </div>
            </form>
          </CardContent>
          <CardFooter className="flex justify-end space-x-4">
            <Button
              onClick={handleUpdate}
              disabled={isSubmitting || !hasChanges}
            >
              {isSubmitting ? "Saving..." : "Submit"}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                // Preserve userId in the URL when navigating back to time-off
                if (isSuperUserAccount && selectedUserId !== "current") {
                  navigate(`/time-off?userId=${selectedUserId}`);
                } else {
                  navigate("/time-off");
                }
              }}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default PolicyConfig;
